import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { NodeAvatar, NodeAssociations } from '@/components/nodes'
import { UserHoverCard } from '@/components/users/UserHoverCard'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Message } from '@/types'
import { MessageStatus } from '@/types/enums'
import {
  Reply,
  Trash2,
  Archive,
  MoreVertical,
  Users,
  User,
  ClipboardList,
  CheckSquare,
  Boxes,
  CornerDownRight,
  Mail,
  Eye,
  EyeOff,
  Pencil,
  FileText,
} from 'lucide-react'
import { toast } from 'sonner'
import { messageService } from '@/services'
import { useAuth } from '@/hooks/useAuth'

interface MessageDetailsProps {
  message: Message
  onReply?: () => void
  onEdit?: () => void
  onDelete?: () => void
  onParentClick?: (parent: Message) => void
  showActions?: boolean
}

export function MessageDetails({
  message,
  onReply,
  onEdit,
  onDelete,
  onParentClick,
  showActions = true,
}: MessageDetailsProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [parentMessage, setParentMessage] = useState<Message | null>(null)
  const [loadingParent, setLoadingParent] = useState(true)
  const isCreator = user?.id === message.creator.id
  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin'
  const canEdit = isCreator
  const canDelete = isCreator || isAdmin

  useEffect(() => {
    async function fetchParent() {
      setLoadingParent(true)
      try {
        const parent = await messageService.findParentMessage(message.id)
        setParentMessage(parent)
      } catch (error) {
        console.error('Failed to fetch parent message:', error)
      } finally {
        setLoadingParent(false)
      }
    }

    fetchParent()
  }, [message.id])

  const handleArchive = async () => {
    setLoading(true)
    try {
      if (message.status === MessageStatus.Archived) {
        await messageService.unarchiveMessage(message.id)
        toast.success('Message unarchived')
      } else {
        await messageService.archiveMessage(message.id)
        toast.success('Message archived')
      }
    } catch (error) {
      console.error('Failed to archive message:', error)
      toast.error('Failed to archive message')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this message?')) return

    setLoading(true)
    try {
      await messageService.deleteMessage(message.id)
      toast.success('Message deleted')
      onDelete?.()
    } catch (error) {
      console.error('Failed to delete message:', error)
      toast.error('Failed to delete message')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async () => {
    if (!user) return

    setLoading(true)
    try {
      await messageService.markAsRead(message.id, user.id)
      toast.success('Message marked as read')
      // Trigger a refresh if needed
      window.location.reload()
    } catch (error) {
      console.error('Failed to mark as read:', error)
      toast.error('Failed to mark as read')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsUnread = async () => {
    if (!user) return

    setLoading(true)
    try {
      await messageService.markAsUnread(message.id, user.id)
      toast.success('Message marked as unread')
      // Trigger a refresh if needed
      window.location.reload()
    } catch (error) {
      console.error('Failed to mark as unread:', error)
      toast.error('Failed to mark as unread')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = () => {
    const variants: Record<
      MessageStatus,
      { variant: 'default' | 'secondary' | 'outline' | 'destructive'; label: string }
    > = {
      [MessageStatus.Draft]: { variant: 'secondary', label: 'Draft' },
      [MessageStatus.Published]: { variant: 'default', label: 'Published' },
      [MessageStatus.Archived]: { variant: 'outline', label: 'Archived' },
      [MessageStatus.Deleted]: { variant: 'destructive', label: 'Deleted' },
    }

    const config = variants[message.status]
    return (
      <Badge variant={config.variant} className="ml-2">
        {config.label}
      </Badge>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-6">
            {/* Parent Message Preview (if this is a reply) */}
            {!loadingParent && parentMessage && (
              <div className="border-l-2 border-muted-foreground/30 pl-3 py-2 bg-muted/30 rounded-r">
                <div className="flex items-start gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 mb-1">
                      <CornerDownRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      <p className="text-xs text-muted-foreground">Reply to:</p>
                    </div>
                    <div
                      className="cursor-pointer rounded p-2"
                      onClick={() => onParentClick?.(parentMessage)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <NodeAvatar node={parentMessage.creator} size="sm" />
                        <span className="font-medium">{parentMessage.creator.name}</span>
                        <span className="text-xs text-muted-foreground">
                          on {format(new Date(parentMessage.createdAt), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <p className="line-clamp-2">{parentMessage.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Message Type Badge */}
            <div className="flex items-center gap-2">
              {message.directMessage ? (
                <Badge variant="secondary" className="gap-1">
                  <User className="h-3 w-3" />
                  Direct Message
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <Users className="h-3 w-3" />
                  Group Message
                </Badge>
              )}
              {getStatusBadge()}
            </div>

            {/* Header */}
            <header className="space-y-2">
              <h1 className="text-3xl lg:text-4xl font-bold mb-4">{message.name}</h1>

              {/* From and To Section */}
              <div className="space-y-2">
                {/* From Section */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-muted-foreground min-w-[4rem]">
                    From:
                  </span>
                  <div className="flex items-center gap-3 text-sm">
                    <UserHoverCard user={message.creator}>
                      <button className="flex items-center gap-3 hover:text-foreground">
                        <NodeAvatar node={message.creator} size="sm" />
                        <p className="font-medium text-foreground">{message.creator.name}</p>
                      </button>
                    </UserHoverCard>
                  </div>
                </div>

                {/* To Section */}
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-muted-foreground min-w-[4rem] mt-0.5">
                    To:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {message.users
                      .filter((u) => u.id !== message.creator.id)
                      .map((recipient) => {
                        const hasRead = message.readBy.some((r) => r.userId === recipient.id)
                        return (
                          <UserHoverCard key={recipient.id} user={recipient}>
                            <button className="flex items-center gap-3 text-sm hover:underline">
                              <NodeAvatar node={recipient} size="sm" />
                              <span>{recipient.name}</span>
                              <Badge
                                variant={hasRead ? 'outline' : 'secondary'}
                                className="gap-1 ml-1 text-xs"
                              >
                                {hasRead ? (
                                  <>
                                    <Eye className="h-3 w-3" />
                                    Read
                                  </>
                                ) : (
                                  <>
                                    <Mail className="h-3 w-3" />
                                    Unread
                                  </>
                                )}
                              </Badge>
                            </button>
                          </UserHoverCard>
                        )
                      })}
                  </div>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground min-w-[4rem]">
                  Sent on:
                </span>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(message.createdAt), 'MMM dd, yyyy h:mm a')}
                </span>
              </div>
            </header>
          </div>

          {/* Actions */}
          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={loading}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onReply && (
                  <DropdownMenuItem onClick={onReply}>
                    <Reply className="mr-2 h-4 w-4" />
                    Reply
                  </DropdownMenuItem>
                )}
                {user &&
                  (message.readBy.some((read) => read.userId === user.id) ? (
                    <DropdownMenuItem onClick={handleMarkAsUnread}>
                      <EyeOff className="mr-2 h-4 w-4" />
                      Mark as Unread
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={handleMarkAsRead}>
                      <Eye className="mr-2 h-4 w-4" />
                      Mark as Read
                    </DropdownMenuItem>
                  ))}
                {canEdit && onEdit && message.status === MessageStatus.Draft && (
                  <DropdownMenuItem onClick={onEdit}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleArchive}>
                  <Archive className="mr-2 h-4 w-4" />
                  {message.status === MessageStatus.Archived ? 'Unarchive' : 'Archive'}
                </DropdownMenuItem>
                {canDelete && (
                  <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Message Description */}
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <div className="whitespace-pre-wrap">{message.description}</div>
        </div>

        {/* Associated Nodes */}
        <div className="space-y-1 pt-6 border-t">
          <h2 className="text-md font-medium">Associations</h2>

          {/* Workspace */}
          {message.workspace && (
            <NodeAssociations
              nodes={[message.workspace]}
              nodeType="workspaces"
              maxVisible={1}
              showViewAll={false}
              icon={Boxes}
              label="Workspace:"
            />
          )}

          {/* Projects */}
          {message.projects.length > 0 && (
            <NodeAssociations
              nodes={message.projects}
              nodeType="projects"
              maxVisible={5}
              showViewAll={false}
              icon={ClipboardList}
            />
          )}

          {/* Tasks */}
          {message.tasks.length > 0 && (
            <NodeAssociations
              nodes={message.tasks}
              nodeType="tasks"
              maxVisible={5}
              showViewAll={false}
              icon={CheckSquare}
            />
          )}

          {/* Documents */}
          {message.documents.length > 0 && (
            <NodeAssociations
              nodes={message.documents}
              nodeType="documents"
              maxVisible={5}
              showViewAll={false}
              displayMode="card"
              icon={FileText}
            />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
